/**
 * Button — атом дизайн-системы AgentCEO (ADR-0007).
 *
 * 4 variants × 3 sizes × 9 states (default/hover/active/disabled/loading/focus
 * визуально, остальные — поведенчески через aria/data-attrs).
 *
 * shadcn/ui pattern: copy-paste, CVA для variants, Radix Slot для asChild.
 *
 * Usage:
 *   <Button variant="primary" size="md" loading={isPending}>Создать</Button>
 *   <Button variant="ghost" size="sm" leftIcon={<Icon name="arrow-left"/>}>Назад</Button>
 *   <Button asChild variant="primary"><a href="/auth/login">Войти</a></Button>
 */
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Icon } from '@/components/icons/Icon'

const button = cva(
  // Base styles
  [
    'inline-flex items-center justify-center gap-2 rounded-lg font-semibold',
    'transition-colors',
    'disabled:cursor-not-allowed',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface-canvas',
    'aria-busy:cursor-wait',
  ],
  {
    variants: {
      variant: {
        primary: [
          'bg-action-primary-bg text-text-on-brand',
          'hover:bg-action-primary-bg-hover',
          'active:bg-action-primary-bg-pressed',
          'disabled:bg-action-primary-bg-disabled disabled:text-text-disabled',
        ],
        secondary: [
          'bg-action-secondary-bg text-text-primary border border-action-secondary-border',
          'hover:bg-action-secondary-bg-hover',
          'disabled:bg-surface-subtle disabled:text-text-disabled disabled:border-border',
        ],
        ghost: [
          'bg-transparent text-accent-brand-primary',
          'hover:bg-surface-subtle',
          'disabled:text-text-disabled',
        ],
        destructive: [
          'bg-action-destructive-bg text-text-on-brand',
          'hover:bg-action-destructive-bg-hover',
          'disabled:bg-action-primary-bg-disabled disabled:text-text-disabled',
        ],
        link: [
          'bg-transparent text-text-link p-0 h-auto',
          'hover:underline underline-offset-2',
          'disabled:text-text-disabled disabled:no-underline',
        ],
      },
      size: {
        sm: 'h-8 px-3 text-sm',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-5 text-base',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    compoundVariants: [
      // link variant ignores size paddings (inline)
      { variant: 'link', size: 'sm', class: 'h-auto p-0 text-sm' },
      { variant: 'link', size: 'md', class: 'h-auto p-0 text-sm' },
      { variant: 'link', size: 'lg', class: 'h-auto p-0 text-base' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
      fullWidth: false,
    },
  }
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  asChild?: boolean
  loading?: boolean
  leftIcon?: ReactNode
  rightIcon?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant,
    size,
    fullWidth,
    asChild = false,
    loading = false,
    leftIcon,
    rightIcon,
    disabled,
    children,
    ...rest
  },
  ref
) {
  const isDisabled = disabled || loading

  // asChild: Radix Slot требует ровно ОДИН React-child. Передаём `children` как есть,
  // loading/leftIcon/rightIcon с asChild не поддерживаем (ожидается обычная ссылка).
  if (asChild) {
    return (
      <Slot
        ref={ref}
        aria-disabled={isDisabled || undefined}
        aria-busy={loading || undefined}
        className={button({ variant, size, fullWidth, className })}
        {...rest}
      >
        {children}
      </Slot>
    )
  }

  return (
    <button
      ref={ref}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={button({ variant, size, fullWidth, className })}
      {...rest}
    >
      {loading ? (
        <Icon name="loader-2" size={16} aria-hidden="true" className="animate-spin" />
      ) : leftIcon ? (
        <span aria-hidden="true" className="inline-flex items-center justify-center">{leftIcon}</span>
      ) : null}
      {children}
      {!loading && rightIcon ? (
        <span aria-hidden="true" className="inline-flex items-center justify-center">{rightIcon}</span>
      ) : null}
    </button>
  )
})
